import { FormControl, Grid, InputLabel, Link, MenuItem, Select, TextField, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory } from 'react-router';

import { DataAPIContext, GET_ONE, UPDATE } from '../api';
import BlockRedirect from '../components/BlockRedirect';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import { ContextStore } from '../store/contextStore';
import { isOk, serverErrorMessage } from '../utils';
import { appSupportContacts } from './utils';

// Copy the mobile apps ship for the "Find my booking" screen. Shown as
// placeholders only -- an empty field is saved as empty, and the app falls back
// to its own built-in string. Kept in sync with
// Tour_Operator.FIND_MY_TRIP_TEXT_DEFAULTS on the backend.
const FIND_MY_BOOKING_DEFAULTS = {
    find_my_trip_header_text: 'Please enter your booking number and last name',
    find_my_trip_ref_label: 'Booking Ref.',
    find_my_trip_name_label: 'Last Name',
    find_my_trip_error_text:
        "We can't find a booking matching your details. Please check that your last name and booking number are correct. If your booking is starting soon, contact us via chat for assistance.",
};

const useStyles = makeStyles((theme) => ({
    root: {
        border: `1px solid ${grey[200]}`,
        width: '75%',
        borderRadius: '10px',
        marginTop: theme.spacing(2),
        paddingTop: 0,
        margin: '1rem',
        [theme.breakpoints.down('sm')]: {
            margin: 0,
            padding: '0',
            width: '99%',
            overflow: 'auto',
            height: 'calc(100svh - 8rem)',
        },
    },
    innerGridRoot: {
        margin: theme.spacing(2),
        padding: '1rem',
        [theme.breakpoints.down('sm')]: {
            padding: '1rem',
        },
    },
    buttonPanel: {
        backgroundColor: grey[100],
        marginTop: '2rem !important',
        [theme.breakpoints.down('sm')]: {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: theme.spacing(8),
        },
    },
}));

const TitleTexts = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'tour_operator';
    const classes = useStyles();
    const { tourOperator, updateTourOperator, hasPerm } = React.useContext(ContextStore);
    const history = useHistory();
    const [top, setTop] = React.useState(tourOperator);
    const [loadedTop, setLoadedTop] = React.useState(JSON.stringify(tourOperator));
    const [syncActive, setSyncActive] = React.useState(false);
    const mounted = React.useRef(true);
    const { enqueueSnackbar } = useSnackbar();
    const hasWritePerm = hasPerm('tour_operator', 'write')

    React.useEffect(() => {
        let active = true;
        setSyncActive(true);

        dataAPI(GET_ONE, resource, { id: tourOperator.id })
            .then((res) => {
                if (!active) {
                    return;
                }
                if (!isOk(res)) {
                    enqueueSnackbar(serverErrorMessage(res, 'Error loading title texts'), { variant: 'error' });
                    return;
                }
                setTop(res.data);
                setLoadedTop(JSON.stringify(res.data));
                updateTourOperator(res.data);
            })
            .catch((err) => {
                enqueueSnackbar('Error loading title texts', { variant: 'error' });
            })
            .finally(() => {
                if (active) {
                    setSyncActive(false);
                }
            });

        return () => {
            active = false;
            mounted.current = false;
        };
    }, []);



    const saveChanges = () => {
        let active = true;
        setSyncActive(true);

        // sdtc WP-C: this page never edits the default tag categories, and the list it loaded,
        // sent back whole, would revert changes made to them elsewhere since.
        const { default_tag_categories, ...data } = top;
        dataAPI(UPDATE, resource, { id: top.id, data })
            .then((res) => {
                console.log('top res', res, mounted.current);
                // A refused save resolves with the error body: keep the edits on screen
                if (!isOk(res)) {
                    enqueueSnackbar(serverErrorMessage(res, 'Error saving title texts'), { variant: 'error' });
                    return;
                }
                enqueueSnackbar('Title texts saved', { variant: 'success' });
                if (!mounted.current) {
                    return;
                }
                setTop(res.data);
                setLoadedTop(JSON.stringify(res.data));
                updateTourOperator(res.data);
            })
            .catch((err) => {
                enqueueSnackbar('Error saving title texts', { variant: 'error' });
            })
            .finally(() => {
                if (active) {
                    setSyncActive(false);
                }
            });
    };

    const isDirty = () => JSON.stringify(top) !== loadedTop;

    const onSave = (ev) => {
        if (isDirty()) {
            saveChanges(top);
        }
    };
    const onCancel = (ev) => {
        setTop(JSON.parse(loadedTop));
    };

    const onChange = (ev) => {
        console.log('change', ev.target.name, ev.target.value);
        const newTop = { ...top, [ev.target.name]: ev.target.value };
        console.log('newTop', newTop);
        setTop(newTop);
    };

    const dirty = isDirty();

    // Derived from the current values, so the warning and the SAVE gate hold on load and on
    // every change -- they used to update only when the Button behavior select lost focus.
    const supportContactErrors = React.useMemo(() => {
        if (top.cta_button_behavior !== 2) return { missingContacts: false, disabledContacts: false };
        const support_contacts = appSupportContacts(top.support_contacts);
        if (support_contacts.length === 0) return { missingContacts: true, disabledContacts: false };
        if (!support_contacts.some((sp) => sp.enabled)) return { missingContacts: false, disabledContacts: true };
        return { missingContacts: false, disabledContacts: false };
    }, [top.cta_button_behavior, top.support_contacts]);

    return (
        <>
            <FixedLinearProgress visibility={syncActive} />
            <BlockRedirect shouldBlock={dirty} message={'Are you sure you want to leave without saving changes?'} />
            <PageMeta title="Title Texts" />
            <div className={classes.root}>
            <Grid item xs={12} sm={12} container direction="row" spacing={2} className={classes.innerGridRoot}>
                <Grid item xs={12} sx={{ pb: '0 !important' }}>
                    <PageHeading>Custom Titles</PageHeading>
                </Grid>
                <Grid item xs={12} sx={{ pt: '8px !important' }}>
                        <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Header Title" inputProps={{ maxLength: 40 }} helperText={`${40 - (top.search_bar_title?.length ?? 0)} Characters left`} fullWidth value={top.search_bar_title ?? ''} name="search_bar_title" onChange={onChange} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12}>
                        <TextField
                            disabled={!hasWritePerm}
                            variant="filled"
                            inputProps={{ maxLength: 40 }}
                            helperText={`${40 - (top.search_bar_text?.length ?? 0)} Characters left`}
                            size="small"
                            label="Search Bar Placeholder"
                            fullWidth
                            value={top.search_bar_text ?? ''}
                            name="search_bar_text"
                            onChange={onChange}
                            InputLabelProps={{ shrink: true }}
                        />
                </Grid>
                <Grid item xs={12}>
                        <TextField
                            disabled={!hasWritePerm}
                            variant="filled"
                            inputProps={{ maxLength: 40 }}
                            helperText={`${40 - (top.trip_types_text?.length ?? 0)} Characters left`}
                            size="small"
                            label="Secondary Sort Title"
                            fullWidth
                            value={top.trip_types_text ?? ''}
                            name="trip_types_text"
                            onChange={onChange}
                            InputLabelProps={{ shrink: true }}
                        />
                </Grid>
                <Grid item xs={12} sm={6}>
                        <TextField disabled={!hasWritePerm} variant="filled" size="small" label="Call-to-Action Button Text" fullWidth value={top.cta_button_text ?? ''} name="cta_button_text" onChange={onChange} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl disabled={!hasWritePerm} variant="filled" size="small" fullWidth className={classes.formControl} error={supportContactErrors.missingContacts || supportContactErrors.disabledContacts}>
                        <InputLabel htmlFor="button-behavior-select-element">Button Behavior</InputLabel>
                        <Select value={top.cta_button_behavior} onChange={onChange} inputProps={{ name: 'cta_button_behavior', id: 'button-behavior-select-element' }}>
                            <MenuItem key={0} value={0}>
                                Link via In-App Browser
                            </MenuItem>
                            <MenuItem key={1} value={1}>
                                Link via Device Browser
                            </MenuItem>
                            <MenuItem key={2} value={2}>
                                Phone Number
                            </MenuItem>
                            {/* <MenuItem key={3} value={3}>Assistant Chat</MenuItem> */}
                        </Select>
                    </FormControl>
                    {supportContactErrors.missingContacts && (
                        <Typography color="secondary">
                            No Support Contact is added yet. <Link onClick={() => history.push('/settings/general')}>Add Support Contact(s)</Link>
                        </Typography>
                    )}
                    {supportContactErrors.disabledContacts && (
                        <Typography color="secondary">
                            Support Contact is currently disabled. <Link onClick={() => history.push('/settings/general')}>Enable Support Contact(s)</Link>
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="h6">Find My Booking</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Wording of the screen where a guest looks up their booking in the app. Leave a field empty to keep the wording shown as its placeholder.
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        disabled={!hasWritePerm}
                        variant="filled"
                        size="small"
                        label="Header"
                        placeholder={FIND_MY_BOOKING_DEFAULTS.find_my_trip_header_text}
                        inputProps={{ maxLength: 80 }}
                        helperText={`${80 - (top.find_my_trip_header_text?.length ?? 0)} Characters left`}
                        fullWidth
                        value={top.find_my_trip_header_text ?? ''}
                        name="find_my_trip_header_text"
                        onChange={onChange}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        disabled={!hasWritePerm}
                        variant="filled"
                        size="small"
                        label="Booking Reference Field Label"
                        placeholder={FIND_MY_BOOKING_DEFAULTS.find_my_trip_ref_label}
                        inputProps={{ maxLength: 40 }}
                        helperText={`${40 - (top.find_my_trip_ref_label?.length ?? 0)} Characters left`}
                        fullWidth
                        value={top.find_my_trip_ref_label ?? ''}
                        name="find_my_trip_ref_label"
                        onChange={onChange}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        disabled={!hasWritePerm}
                        variant="filled"
                        size="small"
                        label="Name Field Label"
                        placeholder={FIND_MY_BOOKING_DEFAULTS.find_my_trip_name_label}
                        inputProps={{ maxLength: 40 }}
                        helperText="Guests can enter a last name or a full name either way -- this only changes the label"
                        fullWidth
                        value={top.find_my_trip_name_label ?? ''}
                        name="find_my_trip_name_label"
                        onChange={onChange}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        disabled={!hasWritePerm}
                        variant="filled"
                        size="small"
                        multiline
                        minRows={3}
                        label="&quot;Booking Not Found&quot; Message"
                        placeholder={FIND_MY_BOOKING_DEFAULTS.find_my_trip_error_text}
                        inputProps={{ maxLength: 400 }}
                        helperText={`${400 - (top.find_my_trip_error_text?.length ?? 0)} Characters left`}
                        fullWidth
                        value={top.find_my_trip_error_text ?? ''}
                        name="find_my_trip_error_text"
                        onChange={onChange}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
            </Grid>
            <Grid item xs={12} className={classes.buttonPanel} container alignItems="center">
                <FormActionButtons
                    mode="edit"
                    size="medium"
                    onSubmit={onSave}
                    submitDisabled={syncActive || !dirty || supportContactErrors.missingContacts || supportContactErrors.disabledContacts}
                    onCancel={onCancel}
                    cancelDisabled={syncActive || !dirty}
                    sx={{ flex: 1, my: 2, mx: 3 }}
                />
            </Grid>
        </div>
        </>
    );
};

export default TitleTexts;
